import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, ArrowLeft, Lock, Download } from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    if (error.response?.status === 401 && !original._retry && !String(original.url).includes("/auth/")) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch (_) {
        /* fall through */
      }
    }
    return Promise.reject(error);
  }
);

const detailToText = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).join(" ");
  return "Une erreur est survenue. Réessayez.";
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

const RSVP_STATUS = {
  pending: { label: "En attente", cls: "border-sand/40 text-sand" },
  accepted: { label: "Acceptée", cls: "border-emerald-400/50 text-emerald-400" },
  declined: { label: "Refusée", cls: "border-red-400/50 text-red-400" },
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(detailToText(err.response?.data?.detail) || "Connexion impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border border-gold/20 bg-ink2 p-8 sm:p-10">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">
          <Lock size={12} /> Accès privé
        </p>
        <h1 className="mt-4 font-serif text-3xl font-light text-parchment">Liste des Invités</h1>
        <p className="mt-3 text-sm font-light leading-relaxed text-sand">
          Réservé à la Maison. Connectez-vous pour consulter les demandes d'invitation et les messages.
        </p>
        <label className="mt-8 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">Courriel</label>
        <input
          data-testid="admin-login-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full border border-gold/20 bg-ink px-4 py-3 text-sm text-parchment outline-none transition-colors focus:border-gold"
        />
        <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand">Mot de passe</label>
        <input
          data-testid="admin-login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full border border-gold/20 bg-ink px-4 py-3 text-sm text-parchment outline-none transition-colors focus:border-gold"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="mt-8 w-full rounded-full border border-gold/60 px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-50"
        >
          {busy ? "Connexion…" : "Se connecter"}
        </button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sand transition-colors hover:text-gold">
          <ArrowLeft size={12} /> Retour au site
        </Link>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("rsvps");
  const [rsvps, setRsvps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, b, c] = await Promise.all([api.get("/admin/rsvps"), api.get("/admin/bookings"), api.get("/admin/contacts")]);
      setRsvps(r.data);
      setBookings(b.data);
      setContacts(c.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      onLogout();
    }
  };

  const items = tab === "rsvps" ? rsvps : tab === "bookings" ? bookings : contacts;

  const setRsvpStatus = async (id, status) => {
    try {
      await api.patch(`/admin/rsvps/${id}`, { status });
      setRsvps((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      toast.error("Impossible de mettre à jour le statut. Réessayez.");
    }
  };

  const exportCsv = () => {
    const defs = {
      rsvps: [["Date", "created_at"], ["Nom", "name"], ["Courriel", "email"], ["Convives", "guests"], ["Statut", "status"], ["Message", "message"]],
      bookings: [["Date", "created_at"], ["Nom", "name"], ["Courriel", "email"], ["Événement", "event_title"], ["Date de l'événement", "event_date"], ["Places", "seats"], ["Message", "message"]],
      contacts: [["Date", "created_at"], ["Nom", "name"], ["Courriel", "email"], ["Objet", "subject"], ["Message", "message"]],
    };
    const cols = defs[tab];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(([h]) => esc(h)).join(";")];
    items.forEach((it) =>
      lines.push(
        cols
          .map(([, k]) =>
            esc(k === "created_at" ? fmtDate(it[k]) : k === "status" ? RSVP_STATUS[it.status || "pending"].label : it[k])
          )
          .join(";")
      )
    );
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `maison-${tab === "rsvps" ? "invitations" : tab === "bookings" ? "reservations" : "messages"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8 lg:py-16">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">
            <Lock size={12} /> Accès privé
          </p>
          <h1 className="mt-3 font-serif text-3xl font-light text-parchment sm:text-4xl">Liste des Invités</h1>
          <p className="mt-2 text-sm font-light text-sand">Demandes d'invitation à l'inauguration & messages reçus.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="flex items-center gap-2 rounded-full border border-gold/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand transition-colors hover:border-gold hover:text-gold"
          >
            <ArrowLeft size={12} /> Le site
          </Link>
          <button
            data-testid="admin-export-csv-btn"
            onClick={exportCsv}
            disabled={loading || items.length === 0}
            className="flex items-center gap-2 rounded-full border border-gold/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand transition-colors hover:border-gold hover:text-gold disabled:opacity-40"
          >
            <Download size={12} /> Exporter CSV
          </button>
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 rounded-full border border-gold/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand transition-colors hover:border-gold hover:text-gold"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-gold/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
          >
            <LogOut size={12} /> Déconnexion
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-gold/15">
        {[
          ["rsvps", `Invitations (${rsvps.length})`],
          ["bookings", `Réservations (${bookings.length})`],
          ["contacts", `Messages (${contacts.length})`],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
              tab === key ? "border-b-2 border-gold text-gold" : "text-sand hover:text-parchment"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {loading && <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sand">Chargement…</p>}
        {!loading && items.length === 0 && (
          <p data-testid="admin-empty-state" className="font-serif text-lg font-light italic text-sand">
            {tab === "rsvps"
              ? "Aucune demande d'invitation pour l'instant."
              : tab === "bookings"
              ? "Aucune réservation pour l'instant."
              : "Aucun message pour l'instant."}
          </p>
        )}
        {!loading &&
          items.map((item) => (
            <article
              key={item.id}
              data-testid={tab === "rsvps" ? "admin-rsvp-row" : tab === "bookings" ? "admin-booking-row" : "admin-contact-row"}
              className="border border-gold/15 bg-ink2 p-6"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-serif text-xl font-light text-parchment">{item.name}</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/70">{fmtDate(item.created_at)}</span>
              </div>
              <p className="mt-1 font-mono text-[11px] tracking-wide text-gold">
                <a href={`mailto:${item.email}`} className="hover:underline">{item.email}</a>
                {tab === "rsvps" && item.guests && <span className="ml-4 text-sand">Convives : {item.guests}</span>}
                {tab === "bookings" && <span className="ml-4 text-sand">Places : {item.seats}</span>}
                {tab === "contacts" && item.subject && <span className="ml-4 text-sand">Objet : {item.subject}</span>}
              </p>
              {tab === "bookings" && (
                <p className="mt-2 font-serif text-base font-light italic text-parchment/85">
                  {item.event_title} — {item.event_date}
                </p>
              )}
              {item.message && <p className="mt-3 text-sm font-light leading-relaxed text-parchment/85">{item.message}</p>}
              {tab === "rsvps" && (
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gold/10 pt-4">
                  <span
                    data-testid="rsvp-status-badge"
                    className={`rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${RSVP_STATUS[item.status || "pending"].cls}`}
                  >
                    {RSVP_STATUS[item.status || "pending"].label}
                  </span>
                  {(item.status || "pending") !== "accepted" && (
                    <button
                      data-testid="rsvp-accept-btn"
                      onClick={() => setRsvpStatus(item.id, "accepted")}
                      className="rounded-full border border-emerald-400/40 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 transition-colors hover:bg-emerald-400/10"
                    >
                      Accepter
                    </button>
                  )}
                  {(item.status || "pending") !== "declined" && (
                    <button
                      data-testid="rsvp-decline-btn"
                      onClick={() => setRsvpStatus(item.id, "declined")}
                      className="rounded-full border border-red-400/40 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-red-400 transition-colors hover:bg-red-400/10"
                    >
                      Refuser
                    </button>
                  )}
                  {(item.status || "pending") !== "pending" && (
                    <button
                      data-testid="rsvp-reset-btn"
                      onClick={() => setRsvpStatus(item.id, "pending")}
                      className="rounded-full border border-gold/25 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-sand transition-colors hover:border-gold hover:text-gold"
                    >
                      Remettre en attente
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ink text-parchment">
      {user === null && (
        <div className="flex min-h-screen items-center justify-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sand">Chargement…</p>
        </div>
      )}
      {user === false && <LoginForm onLogin={setUser} />}
      {user && <Dashboard onLogout={() => setUser(false)} />}
    </div>
  );
}
